import { useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDocument } from '@components/icons/document';
import { IconDropside } from '@components/icons/dropside';
import { Plan } from '@components/plan';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { PlanData } from 'api/ai-builder/requests';

type PlanStatus = 'pending' | 'accepted' | 'rejected';

type AiBuilderPlanProps = {
  plan: PlanData;
  status: PlanStatus;
  onAccept: () => Promise<void>;
  isLoading?: boolean;
};

export function AiBuilderPlan({ plan, status, onAccept, isLoading }: AiBuilderPlanProps) {
  const { t } = useScopedTranslate(locale);
  const [isExpanded, setIsExpanded] = useState(true);

  const isPending = status === 'pending';
  const isAccepted = status === 'accepted';
  const isRejected = status === 'rejected';
  const hasTasks = plan.tasks && plan.tasks.length > 0;

  if (isAccepted) {
    return (
      <Plan
        id={plan.header}
        title={plan.header}
        description={plan.shortDescription}
        todos={plan.tasks.map((task) => ({
          id: task.id,
          label: task.name,
          status: task.status,
          description: task.description,
        }))}
      />
    );
  }

  return (
    <PlanCard>
      <Stack orientation="horizontal" align="center" justify="space-between">
        <Stack orientation="horizontal" space={2} align="center">
          <IconDocument color="buttonIconPositive" size={16} />
          <Text font="semibold14" text={plan.header} color="textPrimary" />
        </Stack>
        <Stack orientation="horizontal" space={2} align="center">
          {isRejected && (
            <Tag
              variant="red"
              text={t('rejected')}
              size="small"
              icon={<IconCloseRegular color="elementRed100" size={16} />}
            />
          )}
          {isPending && hasTasks && (
            <IconButton
              icon={<IconDropside />}
              variant="ghost-purple"
              size="sm"
              isRotated={isExpanded}
              degrees={90}
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
            />
          )}
        </Stack>
      </Stack>

      <DescriptionSection>
        <Text font="regular14" text={plan.shortDescription} color="textSecondary" />
      </DescriptionSection>

      {hasTasks && (
        <Collapse isCollapsed={!isExpanded}>
          <KeyStepsSection>
            <Text font="semibold14" text={t('keySteps')} color="textPrimary" />
            <Stack space={1} style={{ marginTop: pxToRem(8) }}>
              {plan.tasks.map((task) => {
                return (
                  <Stack key={task.id} orientation="horizontal" align="center" space={2}>
                    <TaskBullet />
                    <Text font="regular14" text={task.name} color="textPrimary" />
                  </Stack>
                );
              })}
            </Stack>
          </KeyStepsSection>
        </Collapse>
      )}

      {isPending && (
        <Footer>
          <Button
            variant="primary-blue"
            onClick={onAccept}
            text={t('build')}
            isDisabled={isLoading}
          />
        </Footer>
      )}
    </PlanCard>
  );
}

const PlanCard = styled.div({
  width: '100%',
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  padding: pxToRem(16),
  background: theme.color.backgroundPrimary,
});

const DescriptionSection = styled.div({
  marginTop: pxToRem(8),
});

const KeyStepsSection = styled.div({
  marginTop: pxToRem(12),
  paddingTop: pxToRem(4),
});

const TaskBullet = styled.div({
  width: pxToRem(6),
  height: pxToRem(6),
  borderRadius: '50%',
  background: theme.color.textHelper,
  flexShrink: 0,
});

const Footer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'flex-end',
  space: 2,
})({
  marginTop: pxToRem(16),
});

const locale = {
  en: {
    keySteps: 'Key steps',
    build: 'Build',
    rejected: 'Rejected',
  },
};
